import Html from "./html";
import Banners from "./banners";
import AppOptions from "../app/options";
import { BannerPosition } from "../enum/banner";
import BannerZoneModel from "../model/banner_zone";

declare var google: any;

class VideoBanner {

    constructor(
        private html: Html,
        private banners: Banners,
        private options: AppOptions
    ) { }

    play(bannerZone: BannerZoneModel): void {
        this.html.loadVideoBannerElements()
            .then(() => this.videoBannerElementsLoaded(bannerZone));
    }

    onSoundButtonClick(position: number): void {
        const bannerZone = this.findBannerZone(position);
        if (!bannerZone) {
            return
        }

        const selector = this.selector(bannerZone);
        const soundButtonRange = this.html.q(selector + ' .sound-button-range') as HTMLInputElement;

        if (+bannerZone.adsManager.getVolume() > 0) {
            bannerZone.adsManager.setVolume(0);
            this.html.addClass(selector + ' .sound-button-toggle', 'sound-button-0');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-50');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-100');
            soundButtonRange.value = "0"
        } else {
            bannerZone.adsManager.setVolume(1);
            this.html.addClass(selector + ' .sound-button-toggle', 'sound-button-100');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-50');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-0');
            soundButtonRange.value = "1"
        }
    }

    onVolumenChange(position: number, event: any): void {
        const bannerZone = this.findBannerZone(position);
        if (!bannerZone) {
            return
        }

        bannerZone.adsManager.setVolume(event.target.value);
        const selector = this.selector(bannerZone);

        if (+bannerZone.adsManager.getVolume() === 0) {
            this.html.addClass(selector + ' .sound-button-toggle', 'sound-button-0');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-50');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-100');
        } else if (+bannerZone.adsManager.getVolume() > 0.5) {
            this.html.addClass(selector + ' .sound-button-toggle', 'sound-button-100');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-50');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-0');
        } else {
            this.html.addClass(selector + ' .sound-button-toggle', 'sound-button-50');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-0');
            this.html.removeClass(selector + ' .sound-button-toggle', 'sound-button-100');
        }
    }

    onBigPlayButtonClick(position: number): void {
        const bannerZone = this.findBannerZone(position);
        if (!bannerZone) {
            return
        }

        const selector = this.selector(bannerZone);
        const controls = this.html.q(selector + ' .adplayer-controls') as HTMLElement;

        if (controls.style.display === 'none') {
            this.destroyAdsSetups(bannerZone);
            this.videoBannerElementsLoaded(bannerZone)

            return;
        }

        bannerZone.adsManager.resume();
    }

    resume(position: number): void {
        const bannerZone = this.findBannerZone(position);
        if (!bannerZone) {
            return
        }

        bannerZone.adsManager.resume();
    }

    pause(position: number): void {
        const bannerZone = this.findBannerZone(position);
        if (!bannerZone) {
            return
        }

        bannerZone.adsManager.pause();
    }

    private videoBannerElementsLoaded(bannerZone: BannerZoneModel) {
        const selector = this.selector(bannerZone);

        const container = this.html.q(selector + ' .video-container');
        const player = this.html.q(selector + ' .ads-player');

        if (!container || !player || typeof(google) === "undefined" || !google) {
            return;
        }

        // locale for Slovenian is 'sl', not 'si'.
        // see: https://developers.google.com/interactive-media-ads/docs/sdks/html5/client-side/localization
        const locale = this.options.country === 'si' ? 'sl' : this.options.country;
        google.ima.settings.setLocale(locale);

        bannerZone.adDisplayContainer = new google.ima.AdDisplayContainer(container, player);
        bannerZone.adDisplayContainer.initialize();

        bannerZone.adsLoader = new google.ima.AdsLoader(bannerZone.adDisplayContainer);

        // Check if banner is VPAID. If so, enable it.
        if (bannerZone.banner.isVpaid) {
            bannerZone.adsLoader.getSettings().setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.ENABLED);
            bannerZone.adsLoader.getSettings().setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.INSECURE);
            this.html.addClass(selector, 'vpaid');
        }

        // Add event listeners
        bannerZone.adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
            (event: any) => this.onAdsManagerLoaded(event, player, bannerZone, container),
            false
        );

        bannerZone.adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (e: any) => this.onAdError(e, bannerZone), false);

        // Request video ads.
        const adsRequest = new google.ima.AdsRequest();
        adsRequest.adTagUrl = bannerZone.banner.vastUrl;

        // Fake content completion.
        bannerZone.adsLoader.contentComplete();
        bannerZone.adsLoader.requestAds(adsRequest);
    }

    private destroyAdsSetups(bannerZone: BannerZoneModel): void {
        if (!bannerZone.banner.isVpaid) {
            const selector = this.selector(bannerZone);

            this.html.hide(selector + ' .huge-play-button');
            this.html.hide(selector + ' .adplayer-controls');
        }

        if (bannerZone.adsLoader)  { bannerZone.adsLoader.destroy(); }
        if (bannerZone.adsManager) { bannerZone.adsManager.destroy(); }
        if (bannerZone.adDisplayContainer) { bannerZone.adDisplayContainer.destroy(); }
    }

    private onAdError(adErrorEvent: any, bannerZone: BannerZoneModel): void {
        this.destroyAdsSetups(bannerZone);
    }

    protected executeAppendScript(bannerZone: BannerZoneModel): void {
        const iframe = '<iframe src="' + bannerZone.banner.iframeAppendUrl + '" alt="" style="width: 1px; height: 1px; position: absolute; top: -9999px; left: -9999px"></iframe>'

        const selector = this.selector(bannerZone);
        this.html.writeHTML(selector + ' .iframe_placeholder', iframe);
    }

    private onAdsManagerLoaded(adsManagerLoadedEvent: any, player: HTMLElement, bannerZone: BannerZoneModel, container: any): void {
        const selector = this.selector(bannerZone);

        // Get the ads manager.
        bannerZone.adsManager = adsManagerLoadedEvent.getAdsManager(player);

        // Add listeners.
        bannerZone.adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (e: any) => this.onAdError(e, bannerZone));

        bannerZone.adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, () => {
                this.executeAppendScript(bannerZone);

                if (!bannerZone.banner.isVpaid) {
                    this.html.removeClass(selector + ' .huge-play-button', 'end');
                }
            }
        );

        if (!bannerZone.banner.isVpaid) {
            bannerZone.adsManager.addEventListener(google.ima.AdEvent.Type.PAUSED, () => {
                    this.html.show(selector + ' .huge-play-button');
                    this.html.show(selector + ' .play-button');
                    this.html.hide(selector + ' .pause-button');
                }
            );

            bannerZone.adsManager.addEventListener( google.ima.AdEvent.Type.RESUMED, () => {
                    this.html.hide(selector + ' .huge-play-button');
                    this.html.hide(selector + ' .play-button');
                    this.html.show(selector + ' .pause-button');
                }
            );
        }

        bannerZone.adsManager.addEventListener(google.ima.AdEvent.Type.COMPLETE, () => {
            const wrapper = this.html.q(selector);

            if (!bannerZone.banner.isVpaid) {
                this.html.addClass(selector + ' .huge-play-button', 'end');
                this.html.show(selector + ' .huge-play-button');
                this.html.hide(selector + ' .adplayer-controls');
            }

            if (bannerZone.banner.posterUrl) {
                const size = (wrapper ? wrapper.clientWidth : 600) + 'xX';
                player.setAttribute('poster', bannerZone.banner.posterUrl.replace('PLACEHOLDER', size));
            }

            this.close(bannerZone);
        });

        try {
            // Initialize the ads manager.
            bannerZone.adsManager.init(container.clientWidth, 168, google.ima.ViewMode.NORMAL);

            if (bannerZone.banner.isVpaid) {
                bannerZone.adsManager.setVolume(0);
            } else {
                this.html.hide(selector + ' .huge-play-button');
                this.html.show(selector + ' .adplayer-controls');
                this.html.hide(selector + ' .play-button');
                this.html.show(selector + ' .pause-button');

                bannerZone.adsManager.setVolume(0);
            }

            bannerZone.adsManager.start();
        } catch (error) {
            console.log('Ad error - can not initialize ads manager!', error);
        }
    }

    private close(bannerZone: BannerZoneModel): void {
        const selector = this.selector(bannerZone);
        const wrapper = this.html.q(selector);
        const wrapperContainer = this.html.q(selector + '_container');

        if (bannerZone.position !== BannerPosition.BILLBOARD && bannerZone.position !== BannerPosition.BELOW_ARTICLE) {
            return;
        }
        if (!wrapper) {
            return;
        }

        const topOffset = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0);

        const clientHeight = wrapper.clientHeight;

        wrapper.style.height = '0';
        wrapper.style.display = 'none';

        if (window.innerHeight / 2 + topOffset > wrapper.offsetTop + clientHeight) {
            window.scrollTo(0, topOffset - clientHeight);
        }

        if (wrapperContainer) {
            this.html.addClass(wrapperContainer, 'banner-hide');
        }
    }

    private findBannerZone(position: number): BannerZoneModel|null {
        return this.banners.banners.find(bz => bz.position == position) || null;
    }

    private selector(bannerZone: BannerZoneModel, append = ''): string {
        return '#banner_' + bannerZone.position + '_video' + append;
    }
}

export default VideoBanner;